import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Skeleton,
  Tooltip,
  alpha
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';

/**
 * High-performance list for displaying handbooks
 * Features:
 * - Smart search functionality
 * - Smooth animations and interactions
 * - Clean handbook name display
 */
const VirtualizedHandbookList = ({
  handbooks = [],
  selectedHandbook,
  onSelect,
  loading = false,
  error = null,
  favorites = [],
  onToggleFavorite,
  recentHandbooks = [],
  onLoadMore,
  hasMore = false,
  itemHeight = 72,
  enableInfiniteLoading = true,
  showCategories = true
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const loadMoreTimeoutRef = useRef(null);

  // Clean handbook name by removing "- SOS Handbook" suffix
  const cleanHandbookName = useCallback((name) => {
    return name.replace(/ - SOS Handbook$/i, '').replace(/-SOS Handbook$/i, '').trim();
  }, []);

  // Memoized filtered and categorized handbooks
  const processedHandbooks = useMemo(() => {
    let filtered = handbooks;

    // Apply search filter
    if (internalSearchQuery.trim()) {
      const query = internalSearchQuery.toLowerCase().trim();
      filtered = handbooks.filter(handbook =>
        handbook.name.toLowerCase().includes(query) ||
        (handbook.description && handbook.description.toLowerCase().includes(query))
      );
    }

    // Categorize handbooks
    if (showCategories && !internalSearchQuery.trim()) {
      const categories = {
        favorites: filtered.filter(h => favorites.includes(h.id)),
        recent: filtered.filter(h => 
          recentHandbooks.includes(h.id) && !favorites.includes(h.id)
        ),
        all: filtered.filter(h => 
          !favorites.includes(h.id) && !recentHandbooks.includes(h.id)
        )
      };

      const result = [];
      
      if (categories.favorites.length > 0) {
        result.push({ type: 'category', title: 'Favorites', id: 'cat-favorites' });
        result.push(...categories.favorites.map(h => ({ type: 'handbook', ...h })));
      }
      
      if (categories.recent.length > 0) {
        result.push({ type: 'category', title: 'Recently Viewed', id: 'cat-recent' });
        result.push(...categories.recent.map(h => ({ type: 'handbook', ...h })));
      }
      
      if (categories.all.length > 0) {
        result.push({ type: 'category', title: 'All Handbooks', id: 'cat-all' });
        result.push(...categories.all.map(h => ({ type: 'handbook', ...h })));
      }

      return result;
    }

    return filtered.map(h => ({ type: 'handbook', ...h }));
  }, [handbooks, internalSearchQuery, favorites, recentHandbooks, showCategories]);

  // Handle scroll with throttling
  const handleScroll = useCallback((event) => {
    const { scrollTop: newScrollTop, scrollHeight, clientHeight } = event.target;
    
    setScrollTop(newScrollTop);
    
    // Check for infinite loading
    if (enableInfiniteLoading && hasMore && onLoadMore && !isLoadingMore) {
      const scrollBottom = scrollHeight - scrollTop - clientHeight;
      
      if (scrollBottom < 200) { // 200px from bottom
        setIsLoadingMore(true);
        
        // Debounce load more requests
        if (loadMoreTimeoutRef.current) {
          clearTimeout(loadMoreTimeoutRef.current);
        }
        
        loadMoreTimeoutRef.current = setTimeout(() => {
          onLoadMore().finally(() => setIsLoadingMore(false));
        }, 100);
      }
    }
  }, [enableInfiniteLoading, hasMore, onLoadMore, isLoadingMore, scrollTop]);

  // Scroll to specific item
  const scrollToItem = useCallback((index) => {
    if (listRef.current) {
      const scrollTop = index * itemHeight;
      listRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  }, [itemHeight]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (searchFocused) return;

      const handbooks = processedHandbooks.filter(item => item.type === 'handbook');
      const currentIndex = selectedHandbook 
        ? handbooks.findIndex(h => h.id === selectedHandbook.id)
        : -1;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = Math.min(currentIndex + 1, handbooks.length - 1);
        if (handbooks[nextIndex]) {
          onSelect(handbooks[nextIndex]);
          scrollToItem(nextIndex);
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, 0);
        if (handbooks[prevIndex]) {
          onSelect(handbooks[prevIndex]);
          scrollToItem(prevIndex);
        }
      } else if (event.key === 'Enter' && selectedHandbook) {
        // Could trigger fullscreen or other action
        event.preventDefault();
      } else if (event.key === '/' || (event.ctrlKey && event.key === 'f')) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedHandbook, processedHandbooks, onSelect, searchFocused, scrollToItem]);

  // Get file icon based on mime type
  const getFileIcon = useCallback((mimeType) => {
    if (!mimeType) return <InsertDriveFileIcon />;
    
    if (mimeType.includes('pdf')) return <PictureAsPdfIcon />;
    if (mimeType.includes('document') || mimeType.includes('text')) return <DescriptionIcon />;
    if (mimeType.includes('image')) return <ImageIcon />;
    
    return <InsertDriveFileIcon />;
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  }, []);

  // Return text without highlighting
  const highlightText = useCallback((text, query) => {
    return text;
  }, []);

  // Render individual item
  const renderItem = useCallback((item, index) => {
    if (item.type === 'category') {
      return (
        <Box
          key={item.id}
          sx={{
            height: itemHeight,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}
          >
            {item.title}
          </Typography>
        </Box>
      );
    }

    const isSelected = selectedHandbook?.id === item.id;
    const isFavorite = favorites.includes(item.id);
    const isRecent = recentHandbooks.includes(item.id);

    return (
      <ListItem
        key={item.id}
        button
        selected={isSelected}
        onClick={() => onSelect(item)}
        sx={{
          height: itemHeight,
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            transform: 'translateX(4px)'
          },
          '&.Mui-selected': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16)
            }
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {getFileIcon(item.mimeType)}
        </ListItemIcon>
        
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {highlightText(cleanHandbookName(item.name), internalSearchQuery)}
              {isRecent && (
                <Chip
                  label="Recent"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {item.modifiedTime && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.modifiedTime).toLocaleDateString()}
                </Typography>
              )}
              {item.size && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(item.size)}
                  </Typography>
                </>
              )}
            </Box>
          }
          primaryTypographyProps={{
            component: 'div',
            noWrap: true,
            fontWeight: isSelected ? 600 : 400
          }}
          secondaryTypographyProps={{
            component: 'div',
            noWrap: true
          }}
        />
        
        <ListItemSecondaryAction>
          <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
            <IconButton
              edge="end"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(item.id);
              }}
              sx={{
                color: isFavorite ? 'warning.main' : 'action.disabled',
                '&:hover': {
                  color: 'warning.main',
                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1)
                }
              }}
            >
              {isFavorite ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    );
  }, [
    selectedHandbook,
    favorites,
    recentHandbooks,
    internalSearchQuery,
    onSelect,
    onToggleFavorite,
    getFileIcon,
    formatFileSize,
    cleanHandbookName,
    highlightText,
    itemHeight
  ]);

  // Render loading skeleton
  const renderLoadingSkeleton = useCallback(() => {
    return Array.from({ length: 15 }, (_, index) => (
      <ListItem key={`skeleton-${index}`} sx={{ height: itemHeight, px: 2 }}>
        <ListItemIcon>
          <Skeleton variant="circular" width={24} height={24} />
        </ListItemIcon>
        <ListItemText
          primary={<Skeleton variant="text" width="60%" />}
          secondary={<Skeleton variant="text" width="40%" />}
        />
        <ListItemSecondaryAction>
          <Skeleton variant="circular" width={24} height={24} />
        </ListItemSecondaryAction>
      </ListItem>
    ));
  }, [itemHeight]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Input */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          ref={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search handbooks..."
          value={internalSearchQuery}
          onChange={(e) => setInternalSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          data-handbook-search
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.75, mr: 0.5 }}>
                <SearchIcon sx={{ 
                  color: 'text.secondary',
                  fontSize: '1.3rem'
                }} />
              </InputAdornment>
            ),
            endAdornment: internalSearchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setInternalSearchQuery('')}
                  sx={{ mr: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '52px',
              bgcolor: 'background.default',
              borderRadius: '32px',
              border: theme => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
              },
              '&.Mui-focused': {
                boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: 'primary.main'
                }
              }
            }
          }}
        />
      </Box>

      {/* Results count */}
      {internalSearchQuery && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            {processedHandbooks.filter(item => item.type === 'handbook').length} result(s) found
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="error" variant="body2">
            {error.message || 'Failed to load handbooks'}
          </Typography>
        </Box>
      )}

      {/* List */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          overflowX: 'hidden',
          minHeight: 0, // Important for flex scrolling
          '&::-webkit-scrollbar': {
            width: '8px',
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme => alpha(theme.palette.primary.main, 0.2),
            borderRadius: '20px',
            transition: 'background 0.2s ease',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: theme => alpha(theme.palette.primary.main, 0.3),
          }
        }}
        onScroll={handleScroll}
      >
        {loading && processedHandbooks.length === 0 ? (
          <List>{renderLoadingSkeleton()}</List>
        ) : (
          <List sx={{ p: 0 }}>
            {processedHandbooks.map((item, index) => 
              renderItem(item, index)
            )}
            
            {/* Loading more indicator */}
            {isLoadingMore && (
              <ListItem sx={{ height: itemHeight, justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading more...
                </Typography>
              </ListItem>
            )}
          </List>
        )}
      </Box>

      {/* Empty state */}
      {!loading && processedHandbooks.length === 0 && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center'
          }}
        >
          <InsertDriveFileIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {internalSearchQuery ? 'No handbooks found' : 'No handbooks available'}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {internalSearchQuery 
              ? 'Try adjusting your search terms'
              : 'Handbooks will appear here when available'
            }
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VirtualizedHandbookList;